import React, { useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { NotificationMessage } from '../../types';

interface NotificationProps {
    notification: NotificationMessage;
    onDismiss: (id: string) => void;
}

const Notification: React.FC<NotificationProps> = ({ notification, onDismiss }) => {
    const { type, message, id } = notification;

    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(id);
        }, 5000);

        return () => clearTimeout(timer);
    }, [id, onDismiss]);

    const config = {
        success: {
            icon: CheckCircle2,
            bgColor: 'bg-green-50',
            borderColor: 'border-green-500',
            textColor: 'text-green-800',
            iconColor: 'text-green-500',
        },
        error: {
            icon: AlertCircle,
            bgColor: 'bg-red-50',
            borderColor: 'border-red-500',
            textColor: 'text-red-800',
            iconColor: 'text-red-500',
        },
        warning: {
            icon: AlertTriangle,
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-500',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-500',
        },
        info: {
            icon: Info,
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-500',
            textColor: 'text-blue-800',
            iconColor: 'text-blue-500',
        },
    };

    const { icon: Icon, bgColor, borderColor, textColor, iconColor } = config[type];

    return (
        <div className={`${bgColor} ${textColor} border-l-4 ${borderColor} p-4 rounded-lg shadow-lg flex items-start space-x-3 animate-in slide-in-from-right-full`}>
            <Icon className={iconColor} size={20} />
            <p className="flex-1 text-sm font-medium">{message}</p>
            <button
                onClick={() => onDismiss(id)}
                className={`${textColor} hover:opacity-70 transition`}
            >
                <X size={16} />
            </button>
        </div>
    );
};

interface NotificationContainerProps {
    notifications: NotificationMessage[];
    onDismiss: (id: string) => void;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
    notifications,
    onDismiss
}) => {
    if (notifications.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full">
            {notifications.map((notification) => (
                <Notification
                    key={notification.id}
                    notification={notification}
                    onDismiss={onDismiss}
                />
            ))}
        </div>
    );
};

// Hook for managing notifications
export const useNotifications = () => {
    const [notifications, setNotifications] = React.useState<NotificationMessage[]>([]);
    const [history, setHistory] = React.useState<NotificationMessage[]>([]);
    const [unreadCount, setUnreadCount] = React.useState(0);

    // Grid persistence
    useEffect(() => {
        const stored = localStorage.getItem('adidas_notifications_history');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Convert string dates back to Date objects
                const hydrated = parsed.map((n: any) => ({
                    ...n,
                    timestamp: new Date(n.timestamp)
                }));
                setHistory(hydrated);

                // Calculate unread (logic: simple boolean flag or assume all stored are 'read' unless marked otherwise)
                // For simplicity, let's assume persistent ones are read if they are old, 
                // but let's just track unread count separately if needed.
                // For this MVP, let's say "history" is the full list.
            } catch (e) {
                console.error('Error loading notifications', e);
            }
        }
    }, []);

    const addNotification = React.useCallback((
        type: NotificationMessage['type'],
        message: string
    ) => {
        const notification: NotificationMessage = {
            id: `notif-${Date.now()}-${Math.random()}`,
            type,
            message,
            timestamp: new Date(),
        };

        // Show toast
        setNotifications((prev) => [...prev, notification]);

        // Add to history and persist
        setHistory(prev => {
            const updated = [notification, ...prev].slice(0, 50); // Limit to 50
            localStorage.setItem('adidas_notifications_history', JSON.stringify(updated));
            return updated;
        });

        setUnreadCount(c => c + 1);
    }, []);

    const removeNotification = React.useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const clearHistory = React.useCallback(() => {
        setHistory([]);
        setUnreadCount(0);
        localStorage.removeItem('adidas_notifications_history');
    }, []);

    const markAllRead = React.useCallback(() => {
        setUnreadCount(0);
    }, []);

    return {
        notifications,
        history,
        unreadCount,
        addNotification,
        removeNotification,
        clearHistory,
        markAllRead
    };
};
